import { useApolloClient } from "@apollo/client";
import { ModalOpenButton } from "@msys/ui";
import { Lock as LockIcon } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import { useCloseForBiddingRequestMutation } from "../Requests.generated.js";

interface Props {
  requestId: string;
  projectId: string;
  needsConfirm?: boolean;
  refetchQueries?: string[];
}

export const RequestCloseForBiddingButton = ({
  requestId,
  projectId,
  needsConfirm,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate(["Requests", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();

  const [closeRequestForBidding, { loading }] =
    useCloseForBiddingRequestMutation({
      client,
      refetchQueries,
      awaitRefetchQueries: true,
    });

  const handleCloseRequest = async () => {
    try {
      await closeRequestForBidding({
        variables: {
          input: { projectId, requestId },
        },
      });
      enqueueSnackbar(
        t("Request closed for bidding", {
          ns: "Requests",
        })
      );
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  if (needsConfirm) {
    return (
      <ModalOpenButton
        Modal={ConfirmModal}
        modalProps={{
          title: t("Close request for bidding", {
            ns: "Requests",
          }),
          text: t(
            "Bidders will no longer be able to submit quotes for this request. Are you sure you want to close this request?",
            {
              ns: "Requests",
            }
          ),
          confirmButtonLabel: t("Yes, close", {
            ns: "Requests",
          }),
          handleConfirm: handleCloseRequest,
          cancelButtonLabel: t("No", {
            ns: "Global",
          }),
        }}
      >
        <Button
          startIcon={<LockIcon />}
          variant="outlined"
          color="primary"
          loading={loading}
        >
          {t("Close for bidding", {
            ns: "Requests",
          })}
        </Button>
      </ModalOpenButton>
    );
  }

  return (
    <Button
      startIcon={<LockIcon />}
      variant="outlined"
      color="primary"
      loading={loading}
      onClick={handleCloseRequest}
    >
      {t("Close for bidding", {
        ns: "Requests",
      })}
    </Button>
  );
};
